import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import systemActions from '../../../store/system'
import { useLocationGroups, useLocations } from '../../../hooks/locations'
import { useCurrentUser } from '../../../hooks/users'
import MapGL, { Layer, MapRef, NavigationControl, Popup, ScaleControl, Source } from 'react-map-gl'
import ConfigProvider from 'balkerne-core/config'
import api from 'balkerne-core/api'
import mapboxgl from 'mapbox-gl'
import type MapboxDraw from '@mapbox/mapbox-gl-draw'
import * as turf from '@turf/turf'
import DrawControl from '../../userManagement/DrawControl'
import { useSnackbar } from 'notistack'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  FormControlLabel,
  CircularProgress,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Switch,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { CanonicalPeriod, PERIODS } from '../common'
import { colorToGradient } from '../common/color'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import PortfolioSectionTitle from './PortfolioSectionTitle'

type MapMetricFormatKind = 'number' | 'currency'

type PropertyGroup = {
  id: number
  name: string
  description?: string | null
  property_count?: number
  type?: { id: number; type: string; name?: string; description?: string | null }
}

export type NumericMetricSpec = {
  key: string
  label: string
  perPeriod?: boolean
  loading?: boolean
  formatKind?: MapMetricFormatKind
  decimals?: 0 | 1 | 2
  suffix?: string
  getValue: (propertyId: number, period: CanonicalPeriod) => number | null
  renderValue?: (value: number | null, ctx: { currencySymbol: string; period: CanonicalPeriod }) => ReactNode
}

export type PopupFieldSpec = {
  key: string
  label: string
  perPeriod?: boolean
  loading?: boolean
  formatKind?: MapMetricFormatKind
  decimals?: 0 | 1 | 2
  suffix?: string
  getValue: (propertyId: number, period: CanonicalPeriod) => string | number | null
  renderValue?: (value: string | number | null, ctx: { currencySymbol: string; period: CanonicalPeriod }) => ReactNode
}

type DomainConfig = { type: 'auto'; clampPercentiles?: [number, number] } | { type: 'fixed'; min: number; max: number }

type ContinuousGradient = {
  stops: Array<{ offset: number; color: string }>
}

export type FixedColorOption = {
  key: string
  label: string
  type: 'fixed'
  color: string
  default?: boolean
}

export type DiscreteColorOption = {
  key: string
  label: string
  type: 'discrete'
  metric: NumericMetricSpec
  palette: Record<number, string>
  labels: Record<number, string>
  normalize?: (value: number) => number
  default?: boolean
}

export type ContinuousColorOption = {
  key: string
  label: string
  type: 'continuous'
  metric: NumericMetricSpec
  gradient: ContinuousGradient
  domain?: DomainConfig
  default?: boolean
}

export type PortfolioMapColorOption = FixedColorOption | DiscreteColorOption | ContinuousColorOption

export type FixedRadiusOption = {
  key: string
  label: string
  type: 'fixed'
  radiusPx: number
  default?: boolean
}

export type ScaledRadiusOption = {
  key: string
  label: string
  type: 'scaled'
  metric: NumericMetricSpec
  scale?: 'sqrt' | 'linear' | 'log'
  domain?: DomainConfig
  rangePx?: { min: number; max: number }
  default?: boolean
}

export type PortfolioMapRadiusOption = FixedRadiusOption | ScaledRadiusOption

export type MissingDataHideMode = 'none' | 'both' | 'any'

export type PortfolioPropertyMetricMapProps = {
  selectedPeriod: CanonicalPeriod
  title?: string
  height?: number
  mapStyle?: string
  colorOptions?: PortfolioMapColorOption[]
  radiusOptions?: PortfolioMapRadiusOption[]
  popupFields?: PopupFieldSpec[]
  defaultColorKey?: string
  defaultRadiusKey?: string
  onViewProperty?: (propertyId: number) => void
  onConfirmSelection?: (propertyIds: number[]) => void
  onClearSelection?: () => void
  clearSelectionToken?: number
}

export function makeCurrencyMetric(
  spec: Omit<NumericMetricSpec, 'formatKind'> & { formatKind?: never },
): NumericMetricSpec {
  return { ...spec, formatKind: 'currency' }
}

const NO_DATA_COLOR = '#9e9e9e'
const NO_DATA_STROKE = '#7a7a7a'
const NO_COLOR_X = '×'

type LegendTick = { offsetPct: number; label: string }

type ColorCutoff = { kind: 'discrete'; range: [number, number] } | { kind: 'continuous'; range: [number, number] }

function VerticalScaleLegend({
  height,
  barWidth = 28,
  labelWidth = 124,
  ticks,
  bar,
}: {
  height: number
  barWidth?: number
  labelWidth?: number
  ticks: LegendTick[]
  bar: ReactNode
}) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="stretch"
      justifyContent="flex-start"
      sx={{ height, width: 'fit-content', flexShrink: 0 }}>
      <Box sx={{ position: 'relative', width: labelWidth, minWidth: 0 }}>
        {ticks.map((t, i) => {
          const translate = t.offsetPct <= 0 ? '0%' : t.offsetPct >= 100 ? '-100%' : '-50%'
          return (
            <Stack
              key={i}
              direction="row"
              spacing={0}
              alignItems="center"
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${t.offsetPct}%`,
                transform: `translateY(${translate})`,
              }}>
              <Typography
                variant="caption"
                title={t.label}
                noWrap
                sx={{
                  flex: 1,
                  minWidth: 0,
                  pr: 0.5,
                  lineHeight: 1.15,
                  textAlign: 'right',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {t.label}
              </Typography>
            </Stack>
          )
        })}
      </Box>
      <Box
        sx={{
          width: barWidth,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid rgba(15, 23, 42, 0.18)',
          backgroundColor: 'rgba(15, 23, 42, 0.02)',
          flexShrink: 0,
        }}>
        {bar}
      </Box>
    </Stack>
  )
}

function parseNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function formatNumber(n: number, decimals: number = 1): string {
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: decimals,
  }).format(n)
}

function toHex2(n: number): string {
  return Math.min(255, Math.max(0, Math.round(n)))
    .toString(16)
    .padStart(2, '0')
}

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.trim()
  if (!clean.startsWith('#')) return null
  const h = clean.slice(1)
  if (h.length !== 6) return null
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  if (![r, g, b].every(Number.isFinite)) return null
  return { r, g, b }
}

function darkenHex(hex: string, amount: number): string {
  const rgb = parseHexColor(hex)
  if (!rgb) return 'rgba(0,0,0,0.35)'
  const f = 1 - Math.min(Math.max(amount, 0), 1)
  return `#${toHex2(rgb.r * f)}${toHex2(rgb.g * f)}${toHex2(rgb.b * f)}`
}

function interpolateHex(a: string, b: string, t: number): string {
  const ca = parseHexColor(a)
  const cb = parseHexColor(b)
  if (!ca || !cb) return b
  const k = Math.min(Math.max(t, 0), 1)
  const r = ca.r + (cb.r - ca.r) * k
  const g = ca.g + (cb.g - ca.g) * k
  const b2 = ca.b + (cb.b - ca.b) * k
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b2)}`
}

function colorAtOffset(gradient: ContinuousGradient, t: number): string {
  const stops = [...gradient.stops].sort((x, y) => x.offset - y.offset)
  if (stops.length === 0) return NO_DATA_COLOR
  const k = Math.min(Math.max(t, 0), 1)
  if (k <= stops[0].offset) return stops[0].color
  if (k >= stops[stops.length - 1].offset) return stops[stops.length - 1].color
  for (let i = 0; i < stops.length - 1; i++) {
    const s0 = stops[i]
    const s1 = stops[i + 1]
    if (k >= s0.offset && k <= s1.offset) {
      const span = s1.offset - s0.offset || 1
      return interpolateHex(s0.color, s1.color, (k - s0.offset) / span)
    }
  }
  return stops[stops.length - 1].color
}

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return NaN
  const idx = (Math.min(Math.max(p, 0), 100) / 100) * (sorted.length - 1)
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sorted[lo]
  const t = idx - lo
  return sorted[lo] + (sorted[hi] - sorted[lo]) * t
}

function computeDomain(values: number[], config: DomainConfig | undefined): { min: number; max: number } | null {
  const finite = values.filter(v => typeof v === 'number' && Number.isFinite(v))
  if (!finite.length) return null
  if (config?.type === 'fixed') {
    const min = Math.min(config.min, config.max)
    const max = Math.max(config.min, config.max)
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null
    return { min, max }
  }
  const sorted = [...finite].sort((a, b) => a - b)
  const clamp = config?.type === 'auto' ? config.clampPercentiles ?? [5, 95] : [5, 95]
  const min = percentile(sorted, clamp[0])
  const max = percentile(sorted, clamp[1])
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  if (min === max) return { min: sorted[0], max: sorted[sorted.length - 1] }
  return { min, max }
}

function scaleRadius(
  value: number,
  domain: { min: number; max: number },
  range: { min: number; max: number },
  scale: 'sqrt' | 'linear' | 'log' = 'sqrt',
): number {
  const d0 = domain.min
  const d1 = domain.max
  if (!(Number.isFinite(d0) && Number.isFinite(d1)) || d0 === d1) return (range.min + range.max) / 2
  const clamp = Math.min(Math.max(value, Math.min(d0, d1)), Math.max(d0, d1))
  const r0 = range.min
  const r1 = range.max

  let t = 0
  if (scale === 'linear') {
    t = (clamp - d0) / (d1 - d0)
  } else if (scale === 'sqrt' && d0 >= 0 && clamp >= 0) {
    const a = Math.sqrt(d0)
    const b = Math.sqrt(d1)
    const c = Math.sqrt(clamp)
    t = (c - a) / (b - a)
  } else if (scale === 'log' && d0 > 0 && clamp > 0) {
    const a = Math.log(d0)
    const b = Math.log(d1)
    const c = Math.log(clamp)
    t = (c - a) / (b - a)
  } else {
    t = (clamp - d0) / (d1 - d0)
  }

  const k = Math.min(Math.max(t, 0), 1)
  return r0 + k * (r1 - r0)
}

function computeSliderStep(min: number, max: number): number {
  const span = Math.abs(max - min)
  if (!Number.isFinite(span) || span <= 0) return 1
  const rough = span / 120
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const candidates = [1, 2, 5, 10].map(k => k * magnitude)
  return candidates.find(s => s >= rough) ?? candidates[candidates.length - 1]
}

function formatMetricValue(
  value: number | null,
  metric: Pick<NumericMetricSpec, 'formatKind' | 'decimals' | 'suffix'>,
  currencySymbol: string,
): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const decimals = metric.decimals ?? 1
  const base =
    metric.formatKind === 'currency'
      ? `${currencySymbol}${formatNumber(value, decimals)}`
      : formatNumber(value, decimals)
  return metric.suffix ? `${base} ${metric.suffix}` : base
}

function formatPopupFieldValue(
  value: string | number | null,
  field: Pick<PopupFieldSpec, 'formatKind' | 'decimals' | 'suffix'>,
  currencySymbol: string,
): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'number') {
    return formatMetricValue(value, field, currencySymbol)
  }
  const s = value.trim()
  return s.length ? s : '—'
}

export default function PortfolioPropertyMetricMap({
  selectedPeriod,
  title = 'Map View',
  height = 520,
  mapStyle = 'mapbox://styles/balkerne/clmqen49o00bm01ns7ur41hzj',
  colorOptions = [],
  radiusOptions = [],
  popupFields = [],
  defaultColorKey,
  defaultRadiusKey,
  onViewProperty,
  onConfirmSelection,
  onClearSelection,
  clearSelectionToken,
}: PortfolioPropertyMetricMapProps) {
  const theme = useTheme()
  const mapRef = useRef<MapRef | null>(null)
  const drawRef = useRef<MapboxDraw | null>(null)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { groupId } = useSelector((state: RootState) => state.system)
  const locationsEnabled = groupId !== undefined && groupId !== null
  const { data: locations, isLoading: locationsLoading } = useLocations({ group_id: groupId }, locationsEnabled)
  const { currencySymbol } = useCurrentUser()
  const { data: locationGroups, isLoading: locationGroupsLoading, refetch: refetchLocationGroups } = useLocationGroups()

  const properties = useMemo(() => {
    return (locations || [])
      .map((p: any) => {
        const lat = p?.coordinates?.latitude
        const lng = p?.coordinates?.longitude
        if (typeof lat !== 'number' || typeof lng !== 'number') return null
        return {
          id: p.id as number,
          name: p.name || `Property ${p.id}`,
          country: p.address?.country ?? null,
          address: p.address ?? null,
          coordinates: { lat, lng },
          insurance: p.insurance ?? null,
        }
      })
      .filter(Boolean) as Array<{
      id: number
      name: string
      country: string | null
      address: any | null
      coordinates: { lat: number; lng: number }
      insurance: any | null
    }>
  }, [locations])

  const propertyById = useMemo(() => {
    const map = new Map<number, (typeof properties)[number]>()
    properties.forEach(p => map.set(p.id, p))
    return map
  }, [properties])

  const builtInFinancialFields = useMemo<PopupFieldSpec[]>(() => {
    return [
      {
        key: 'fin_revenue',
        label: 'Revenue',
        formatKind: 'currency',
        getValue: propertyId => parseNullableNumber(propertyById.get(propertyId)?.insurance?.revenue),
      },
      {
        key: 'fin_structure',
        label: 'Structure Value',
        formatKind: 'currency',
        getValue: propertyId => parseNullableNumber(propertyById.get(propertyId)?.insurance?.structure),
      },
      {
        key: 'fin_contents',
        label: 'Contents Value',
        formatKind: 'currency',
        getValue: propertyId => parseNullableNumber(propertyById.get(propertyId)?.insurance?.contents),
      },
    ]
  }, [propertyById])

  const builtInRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(() => {
    const rangePx = { min: 6, max: 24 }
    const domain: DomainConfig = { type: 'auto', clampPercentiles: [5, 95] }
    const scale: 'sqrt' = 'sqrt'
    const financialMetric = (key: string, label: string, selector: (p: any) => unknown): NumericMetricSpec => ({
      key,
      label,
      perPeriod: false,
      formatKind: 'currency',
      getValue: propertyId => parseNullableNumber(selector(propertyById.get(propertyId))),
    })
    return [
      { key: 'default', label: 'Default', type: 'fixed', radiusPx: 8 },
      {
        key: 'structure_value',
        label: 'Structure Value',
        type: 'scaled',
        metric: financialMetric('structure_value', 'Structure Value', p => p?.insurance?.structure),
        scale,
        domain,
        rangePx,
      },
      {
        key: 'contents_value',
        label: 'Contents Value',
        type: 'scaled',
        metric: financialMetric('contents_value', 'Contents Value', p => p?.insurance?.contents),
        scale,
        domain,
        rangePx,
      },
      {
        key: 'revenue',
        label: 'Revenue',
        type: 'scaled',
        metric: financialMetric('revenue', 'Revenue', p => p?.insurance?.revenue),
        scale,
        domain,
        rangePx,
      },
    ]
  }, [propertyById])

  const mergedColorOptions = useMemo<PortfolioMapColorOption[]>(() => {
    const fixed: FixedColorOption = {
      key: 'default',
      label: 'Default',
      type: 'fixed',
      color: theme.palette.primary.main,
    }
    const out = [fixed, ...colorOptions]
    const seen = new Set<string>()
    return out.filter(opt => {
      if (seen.has(opt.key)) return false
      seen.add(opt.key)
      return true
    })
  }, [colorOptions, theme.palette.primary.main])

  const mergedRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(() => {
    const out = [...builtInRadiusOptions, ...radiusOptions]
    const seen = new Set<string>()
    return out.filter(opt => {
      if (seen.has(opt.key)) return false
      seen.add(opt.key)
      return true
    })
  }, [builtInRadiusOptions, radiusOptions])

  const resolvedDefaultColorKey = useMemo(() => {
    if (defaultColorKey) return defaultColorKey
    const marked = colorOptions.find(o => o.default)?.key
    return marked ?? colorOptions[0]?.key ?? 'default'
  }, [defaultColorKey, colorOptions])

  const resolvedDefaultRadiusKey = useMemo(() => {
    if (defaultRadiusKey) return defaultRadiusKey
    const marked = radiusOptions.find(o => o.default)?.key
    return marked ?? 'structure_value'
  }, [defaultRadiusKey, radiusOptions])

  const [selectedColorKey, setSelectedColorKey] = useState<string>(() => resolvedDefaultColorKey)
  const [selectedRadiusKey, setSelectedRadiusKey] = useState<string>(() => resolvedDefaultRadiusKey)
  const [hideMode, setHideMode] = useState<MissingDataHideMode>('both')
  const [colorCutoffByKey, setColorCutoffByKey] = useState<Record<string, ColorCutoff>>({})
  const [radiusCutoffByKey, setRadiusCutoffByKey] = useState<Record<string, [number, number]>>({})

  const [drawModeEnabled, setDrawModeEnabled] = useState(false)
  const [selectionPolygon, setSelectionPolygon] = useState<GeoJSON.Feature<GeoJSON.Polygon> | null>(null)
  const [selectAllActive, setSelectAllActive] = useState(false)
  const [selectionConfirmed, setSelectionConfirmed] = useState(false)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignDialogTab, setAssignDialogTab] = useState<'existing' | 'new'>('existing')
  const [existingGroup, setExistingGroup] = useState<PropertyGroup | null>(null)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupDescription, setNewGroupDescription] = useState('')
  const [selectCreatedGroup, setSelectCreatedGroup] = useState(true)
  const [assignSubmitting, setAssignSubmitting] = useState(false)
  const assignDialogInitializedRef = useRef(false)
  const lastClearSelectionTokenRef = useRef<number | null>(null)
  const suppressNextDrawDeleteRef = useRef(false)

  useEffect(() => {
    if (!assignDialogOpen) {
      assignDialogInitializedRef.current = false
      setAssignSubmitting(false)
      return
    }
    refetchLocationGroups?.()
  }, [assignDialogOpen, refetchLocationGroups])

  useEffect(() => {
    if (!assignDialogOpen) return
    if (assignDialogInitializedRef.current) return
    if (locationGroupsLoading) return
    const groups = (locationGroups ?? []) as PropertyGroup[]
    const hasGroups = groups.length > 0
    setAssignDialogTab(hasGroups ? 'existing' : 'new')
    setExistingGroup(hasGroups ? groups[0] : null)
    setNewGroupName('')
    setNewGroupDescription('')
    setSelectCreatedGroup(true)
    assignDialogInitializedRef.current = true
  }, [assignDialogOpen, locationGroups, locationGroupsLoading])

  useEffect(() => {
    if (!assignDialogOpen) return
    if (!existingGroup) return
    const groups = (locationGroups ?? []) as PropertyGroup[]
    if (groups.some(g => g.id === existingGroup.id)) return
    setExistingGroup(groups[0] ?? null)
  }, [assignDialogOpen, existingGroup, locationGroups])

  useEffect(() => {
    if (!mergedColorOptions.some(o => o.key === selectedColorKey)) {
      setSelectedColorKey(resolvedDefaultColorKey)
    }
  }, [mergedColorOptions, resolvedDefaultColorKey, selectedColorKey])

  useEffect(() => {
    if (!mergedRadiusOptions.some(o => o.key === selectedRadiusKey)) {
      setSelectedRadiusKey(resolvedDefaultRadiusKey)
    }
  }, [mergedRadiusOptions, resolvedDefaultRadiusKey, selectedRadiusKey])

  const selectedColorOption = useMemo(() => {
    return mergedColorOptions.find(o => o.key === selectedColorKey) ?? mergedColorOptions[0]
  }, [mergedColorOptions, selectedColorKey])

  const selectedRadiusOption = useMemo(() => {
    return mergedRadiusOptions.find(o => o.key === selectedRadiusKey) ?? mergedRadiusOptions[0]
  }, [mergedRadiusOptions, selectedRadiusKey])

  const renderStaticBadge = useCallback(() => {
    return (
      <Box
        component="span"
        sx={{
          px: 0.75,
          py: 0.1,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1.4,
          backgroundColor: alpha(theme.palette.text.primary, 0.06),
          border: `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
          color: alpha(theme.palette.text.primary, 0.72),
          flexShrink: 0,
        }}>
        Static
      </Box>
    )
  }, [theme.palette.text.primary])

  const renderOptionLabel = useCallback(
    (label: string, perPeriod?: boolean) => {
      return (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {label}
          </Box>
          {perPeriod ? null : renderStaticBadge()}
        </Stack>
      )
    },
    [renderStaticBadge],
  )

  const discreteColorConfig = useMemo(() => {
    if (selectedColorOption.type !== 'discrete') return null
    const values = Object.keys(selectedColorOption.labels)
      .map(Number)
      .filter(v => Number.isFinite(v))
      .sort((a, b) => a - b) // low -> high
    if (!values.length) return null
    const indexByValue = new Map<number, number>()
    values.forEach((v, i) => indexByValue.set(v, i))
    return { valuesAsc: values, indexByValue }
  }, [selectedColorOption])

  const domainForNumericMetric = useCallback(
    (metric: NumericMetricSpec, domain: DomainConfig | undefined) => {
      const ids = properties.map(p => p.id)
      const periods = [selectedPeriod]
      const values: number[] = []
      ids.forEach(id => {
        periods.forEach(period => {
          const v = metric.getValue(id, period)
          if (typeof v === 'number' && Number.isFinite(v)) values.push(v)
        })
      })
      return computeDomain(values, domain)
    },
    [properties, selectedPeriod],
  )

  const colorDomain = useMemo(() => {
    if (!selectedColorOption || selectedColorOption.type !== 'continuous') return null
    return domainForNumericMetric(selectedColorOption.metric, selectedColorOption.domain)
  }, [domainForNumericMetric, selectedColorOption])

  const radiusDomain = useMemo(() => {
    if (!selectedRadiusOption || selectedRadiusOption.type !== 'scaled') return null
    return domainForNumericMetric(selectedRadiusOption.metric, selectedRadiusOption.domain)
  }, [domainForNumericMetric, selectedRadiusOption])

  const colorCutoff = useMemo<ColorCutoff | null>(() => {
    if (selectedColorOption.type === 'fixed') return null
    if (selectedColorOption.type === 'discrete') {
      if (!discreteColorConfig) return null
      const maxIndex = Math.max(0, discreteColorConfig.valuesAsc.length - 1)
      const stored = colorCutoffByKey[selectedColorKey]
      const range: [number, number] = stored?.kind === 'discrete' ? stored.range : [0, maxIndex]
      const lo = Math.min(maxIndex, Math.max(0, Math.round(range[0])))
      const hi = Math.min(maxIndex, Math.max(0, Math.round(range[1])))
      return { kind: 'discrete', range: lo <= hi ? ([lo, hi] as [number, number]) : ([hi, lo] as [number, number]) }
    }
    if (selectedColorOption.type === 'continuous') {
      if (!colorDomain) return null
      const domainMin = Math.min(colorDomain.min, colorDomain.max)
      const domainMax = Math.max(colorDomain.min, colorDomain.max)
      const stored = colorCutoffByKey[selectedColorKey]
      const range: [number, number] = stored?.kind === 'continuous' ? stored.range : [domainMin, domainMax]
      const lo = Math.min(domainMax, Math.max(domainMin, range[0]))
      const hi = Math.min(domainMax, Math.max(domainMin, range[1]))
      return { kind: 'continuous', range: lo <= hi ? ([lo, hi] as [number, number]) : ([hi, lo] as [number, number]) }
    }
    return null
  }, [colorCutoffByKey, colorDomain, discreteColorConfig, selectedColorKey, selectedColorOption])

  const radiusCutoff = useMemo<[number, number] | null>(() => {
    if (selectedRadiusOption.type !== 'scaled') return null
    if (!radiusDomain) return null
    const domainMin = Math.min(radiusDomain.min, radiusDomain.max)
    const domainMax = Math.max(radiusDomain.min, radiusDomain.max)
    const stored = radiusCutoffByKey[selectedRadiusKey]
    const range: [number, number] = stored ?? [domainMin, domainMax]
    const lo = Math.min(domainMax, Math.max(domainMin, range[0]))
    const hi = Math.min(domainMax, Math.max(domainMin, range[1]))
    return lo <= hi ? ([lo, hi] as [number, number]) : ([hi, lo] as [number, number])
  }, [radiusCutoffByKey, radiusDomain, selectedRadiusKey, selectedRadiusOption.type])

  const { featureCollection, stats } = useMemo(() => {
    const features: any[] = []
    let total = 0
    let noColor = 0
    let noRadius = 0
    let shown = 0

    properties.forEach(p => {
      total += 1

      const colorValue =
        selectedColorOption.type === 'fixed' ? null : selectedColorOption.metric.getValue(p.id, selectedPeriod)
      const normalizedDiscrete =
        selectedColorOption.type === 'discrete' && typeof colorValue === 'number' && Number.isFinite(colorValue)
          ? selectedColorOption.normalize
            ? selectedColorOption.normalize(colorValue)
            : Math.round(colorValue)
          : null
      const hasColor =
        selectedColorOption.type === 'fixed'
          ? true
          : selectedColorOption.type === 'discrete'
          ? normalizedDiscrete != null && selectedColorOption.palette[normalizedDiscrete] != null
          : typeof colorValue === 'number' && Number.isFinite(colorValue) && colorDomain != null
      if (!hasColor) noColor += 1

      const radiusValue =
        selectedRadiusOption.type === 'fixed' ? null : selectedRadiusOption.metric.getValue(p.id, selectedPeriod)
      const hasRadius =
        selectedRadiusOption.type === 'fixed'
          ? true
          : typeof radiusValue === 'number' && Number.isFinite(radiusValue) && radiusDomain != null
      if (!hasRadius) noRadius += 1

      const outsideColorCutoff = (() => {
        if (!hasColor || !colorCutoff) return false
        if (selectedColorOption.type === 'discrete' && colorCutoff.kind !== 'discrete') return false
        if (selectedColorOption.type === 'continuous' && colorCutoff.kind !== 'continuous') return false
        if (selectedColorOption.type === 'continuous') {
          if (!colorDomain) return false
          const { min, max } = colorDomain
          const v = colorValue as number
          const clamped = Math.min(Math.max(v, Math.min(min, max)), Math.max(min, max))
          return clamped < colorCutoff.range[0] || clamped > colorCutoff.range[1]
        }
        if (selectedColorOption.type === 'discrete') {
          if (!discreteColorConfig) return false
          const idx = normalizedDiscrete == null ? null : discreteColorConfig.indexByValue.get(normalizedDiscrete)
          if (idx == null) return false
          return idx < colorCutoff.range[0] || idx > colorCutoff.range[1]
        }
        return false
      })()

      const outsideRadiusCutoff = (() => {
        if (!hasRadius || !radiusCutoff) return false
        if (selectedRadiusOption.type !== 'scaled' || !radiusDomain) return false
        const { min, max } = radiusDomain
        const v = radiusValue as number
        const clamped = Math.min(Math.max(v, Math.min(min, max)), Math.max(min, max))
        return clamped < radiusCutoff[0] || clamped > radiusCutoff[1]
      })()

      const shouldHideMissing =
        hideMode === 'none' ? false : hideMode === 'any' ? !hasColor || !hasRadius : !hasColor && !hasRadius
      if (shouldHideMissing || outsideColorCutoff || outsideRadiusCutoff) return
      shown += 1

      // Fill color
      let fillColor = NO_DATA_COLOR
      if (selectedColorOption.type === 'fixed') {
        fillColor = selectedColorOption.color
      } else if (selectedColorOption.type === 'discrete' && hasColor && normalizedDiscrete != null) {
        fillColor = selectedColorOption.palette[normalizedDiscrete] ?? NO_DATA_COLOR
      } else if (selectedColorOption.type === 'continuous' && hasColor && colorDomain) {
        const { min, max } = colorDomain
        const v = colorValue as number
        const t = min === max ? 0.5 : (Math.min(Math.max(v, min), max) - min) / (max - min)
        fillColor = colorAtOffset(selectedColorOption.gradient, t)
      }

      // Radius in px
      let radiusPx = 8
      if (selectedRadiusOption.type === 'fixed') {
        radiusPx = selectedRadiusOption.radiusPx
      } else if (selectedRadiusOption.type === 'scaled') {
        const rangePx = selectedRadiusOption.rangePx ?? { min: 6, max: 24 }
        const scale = selectedRadiusOption.scale ?? 'sqrt'
        if (hasRadius && radiusDomain) {
          radiusPx = scaleRadius(radiusValue as number, radiusDomain, rangePx, scale)
        } else {
          radiusPx = rangePx.min
        }
      }

      const strokeWidth = hasRadius ? 1 : 2.25
      const strokeColor = !hasRadius ? '#ffffff' : hasColor ? darkenHex(fillColor, 0.2) : NO_DATA_STROKE

      features.push({
        type: 'Feature',
        id: p.id,
        properties: {
          id: p.id,
          name: p.name,
          country: p.country,
          hasColor,
          hasRadius,
          fillColor,
          strokeColor,
          strokeWidth,
          radiusPx,
        },
        geometry: {
          type: 'Point',
          coordinates: [p.coordinates.lng, p.coordinates.lat],
        },
      })
    })

    return {
      featureCollection: { type: 'FeatureCollection', features } as any,
      stats: { total, shown, noColor, noRadius },
    }
  }, [
    colorDomain,
    colorCutoff,
    discreteColorConfig,
    hideMode,
    properties,
    radiusCutoff,
    radiusDomain,
    selectedColorOption,
    selectedPeriod,
    selectedRadiusOption,
  ])

  const bounds = useMemo(() => {
    if (!properties.length) return null
    let minLat = properties[0].coordinates.lat
    let maxLat = properties[0].coordinates.lat
    let minLng = properties[0].coordinates.lng
    let maxLng = properties[0].coordinates.lng
    properties.forEach(p => {
      minLat = Math.min(minLat, p.coordinates.lat)
      maxLat = Math.max(maxLat, p.coordinates.lat)
      minLng = Math.min(minLng, p.coordinates.lng)
      maxLng = Math.max(maxLng, p.coordinates.lng)
    })
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ] as [[number, number], [number, number]]
  }, [properties])

  const [mapLoaded, setMapLoaded] = useState(false)
  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!mapLoaded || !map || !bounds) return
    map.fitBounds(bounds, { padding: 60, maxZoom: 8, duration: 900 })
  }, [bounds, mapLoaded])

  const hoveredIdRef = useRef<number | null>(null)
  const [hovered, setHovered] = useState<{ id: number } | null>(null)
  const [selected, setSelected] = useState<{ id: number; lng: number; lat: number } | null>(null)
  const [showHoverOpenHint, setShowHoverOpenHint] = useState(false)
  const hoverHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (drawModeEnabled) {
      setHovered(null)
      setSelected(null)
      setShowHoverOpenHint(false)
      if (hoverHintTimeoutRef.current) {
        clearTimeout(hoverHintTimeoutRef.current)
        hoverHintTimeoutRef.current = null
      }
      return
    }
    setShowHoverOpenHint(false)
    if (hoverHintTimeoutRef.current) {
      clearTimeout(hoverHintTimeoutRef.current)
      hoverHintTimeoutRef.current = null
    }
    if (!hovered || selected) return
    hoverHintTimeoutRef.current = setTimeout(() => {
      setShowHoverOpenHint(true)
    }, 1000)
    return () => {
      if (hoverHintTimeoutRef.current) {
        clearTimeout(hoverHintTimeoutRef.current)
        hoverHintTimeoutRef.current = null
      }
    }
  }, [drawModeEnabled, hovered?.id, selected])

  const handleHover = useCallback(
    (e: mapboxgl.MapLayerMouseEvent) => {
      if (drawModeEnabled) return
      if (selected) return
      const feature = e.features?.[0] as any
      if (!feature) {
        hoveredIdRef.current = null
        setHovered(null)
        return
      }
      const id = Number(feature?.properties?.id ?? feature?.id)
      if (!Number.isFinite(id)) return
      if (hoveredIdRef.current === id) return
      hoveredIdRef.current = id
      setHovered({ id })
    },
    [drawModeEnabled, selected],
  )

  const handleClick = useCallback(
    (e: mapboxgl.MapLayerMouseEvent) => {
      if (drawModeEnabled) return
      const feature = e.features?.[0] as any
      if (!feature) {
        setSelected(null)
        return
      }
      const id = Number(feature?.properties?.id ?? feature?.id)
      if (!Number.isFinite(id)) return
      setHovered(null)
      setSelected({ id, lng: e.lngLat.lng, lat: e.lngLat.lat })
    },
    [drawModeEnabled],
  )

  const layerIds = useMemo(() => ['portfolio-property-circles', 'portfolio-no-color-x'], [])

  const hoverMatchId = hovered?.id ?? -1
  const selectedMatchId = selected?.id ?? -1

  const handleDrawCreateOrUpdate = useCallback((evt: { features: object[] }) => {
    const polygon = (evt.features as any[])?.find(f => f?.geometry?.type === 'Polygon') as
      | GeoJSON.Feature<GeoJSON.Polygon>
      | undefined
    if (!polygon) return
    setSelectAllActive(false)
    setSelectionPolygon(polygon)
    setSelectionConfirmed(false)
  }, [])

  const handleDrawDelete = useCallback(() => {
    setSelectAllActive(false)
    setSelectionPolygon(null)
    setSelectionConfirmed(false)
    if (suppressNextDrawDeleteRef.current) {
      suppressNextDrawDeleteRef.current = false
      return
    }
    onClearSelection?.()
  }, [onClearSelection])

  const selectedPropertyIds = useMemo<number[]>(() => {
    if (!drawModeEnabled) return []
    if (selectAllActive) {
      return (featureCollection.features as any[])
        .map(feature => Number(feature?.properties?.id ?? feature?.id))
        .filter((id): id is number => Number.isFinite(id))
    }
    if (!selectionPolygon) return []
    try {
      const points = turf.pointsWithinPolygon(
        featureCollection as any,
        selectionPolygon as any,
      ) as GeoJSON.FeatureCollection<GeoJSON.Point, any>
      return points.features.map(f => Number(f?.properties?.id)).filter((id): id is number => Number.isFinite(id))
    } catch {
      return []
    }
  }, [drawModeEnabled, featureCollection, selectAllActive, selectionPolygon])

  const hasPendingSelection = selectAllActive || Boolean(selectionPolygon)

  const deleteDrawFeatures = useCallback((suppressParentClear = false) => {
    if (!drawRef.current) return
    suppressNextDrawDeleteRef.current = suppressParentClear
    drawRef.current.deleteAll()
  }, [])

  const clearDrawSelection = useCallback(() => {
    deleteDrawFeatures(true)
    setSelectAllActive(false)
    setSelectionPolygon(null)
    setSelectionConfirmed(false)
    drawRef.current?.changeMode('draw_polygon')
    onClearSelection?.()
  }, [deleteDrawFeatures, onClearSelection])

  const exitDrawMode = useCallback(() => {
    deleteDrawFeatures(true)
    setSelectAllActive(false)
    setSelectionPolygon(null)
    setSelectionConfirmed(false)
    setAssignDialogOpen(false)
    setDrawModeEnabled(false)
    onClearSelection?.()
  }, [deleteDrawFeatures, onClearSelection])

  const selectAllProperties = useCallback(() => {
    deleteDrawFeatures(true)
    setSelectionPolygon(null)
    setSelectAllActive(true)
    setSelectionConfirmed(false)
    drawRef.current?.changeMode('simple_select')
  }, [deleteDrawFeatures])

  useEffect(() => {
    if (typeof clearSelectionToken !== 'number') return
    if (lastClearSelectionTokenRef.current === clearSelectionToken) return
    lastClearSelectionTokenRef.current = clearSelectionToken
    // Keep internal draw state in sync when the parent clears the active subset.
    exitDrawMode()
  }, [clearSelectionToken, exitDrawMode])

  const confirmDrawSelection = useCallback(() => {
    setSelectionConfirmed(true)
    drawRef.current?.changeMode('simple_select')
    onConfirmSelection?.(selectedPropertyIds)
  }, [onConfirmSelection, selectedPropertyIds])

  const sortedGroups = useMemo<PropertyGroup[]>(() => {
    const groups = (locationGroups ?? []) as PropertyGroup[]
    return [...groups].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [locationGroups])

  const canSubmitAssignment = useMemo(() => {
    if (assignSubmitting) return false
    if (selectedPropertyIds.length === 0) return false
    if (assignDialogTab === 'existing') return Boolean(existingGroup?.id)
    return newGroupName.trim().length > 0
  }, [assignDialogTab, assignSubmitting, existingGroup?.id, newGroupName, selectedPropertyIds.length])

  const submitAssignment = useCallback(async () => {
    if (!canSubmitAssignment) return
    setAssignSubmitting(true)
    try {
      let createdGroupId: number | null = null
      if (assignDialogTab === 'existing') {
        if (!existingGroup) return
        await api.post(`/locations/groups/${existingGroup.id}/reassign`, { add: selectedPropertyIds, remove: [] })
        enqueueSnackbar(`Added ${selectedPropertyIds.length.toLocaleString()} properties to "${existingGroup.name}".`, {
          variant: 'success',
        })
      } else {
        const trimmedName = newGroupName.trim()
        const trimmedDescription = newGroupDescription.trim()
        const res = await api.post('/locations/groups', {
          name: trimmedName,
          type: 'region',
          description: trimmedDescription.length ? trimmedDescription : undefined,
          propertyIds: selectedPropertyIds,
        })
        const parsedId = Number(res?.data)
        createdGroupId = Number.isFinite(parsedId) ? parsedId : null
        enqueueSnackbar(
          `Created "${trimmedName}" and assigned ${selectedPropertyIds.length.toLocaleString()} properties.`,
          {
            variant: 'success',
          },
        )
      }
      await refetchLocationGroups?.()
      if (assignDialogTab === 'new' && selectCreatedGroup && createdGroupId != null) {
        exitDrawMode()
        dispatch(systemActions.setGroupId(createdGroupId))
      } else {
        setAssignDialogOpen(false)
      }
    } catch (err: any) {
      const status = err?.response?.status
      const message = err?.response?.data
      if (status === 400 && typeof message === 'string') {
        enqueueSnackbar(message, { variant: 'warning' })
      } else {
        enqueueSnackbar('Failed to assign properties to group.', { variant: 'error' })
      }
    } finally {
      setAssignSubmitting(false)
    }
  }, [
    assignDialogTab,
    canSubmitAssignment,
    dispatch,
    enqueueSnackbar,
    existingGroup,
    exitDrawMode,
    newGroupDescription,
    newGroupName,
    refetchLocationGroups,
    selectedPropertyIds,
    selectCreatedGroup,
  ])

  const selectedMetricLoading = useMemo(() => {
    const colorLoading = selectedColorOption.type === 'fixed' ? false : Boolean(selectedColorOption.metric.loading)
    const radiusLoading = selectedRadiusOption.type === 'fixed' ? false : Boolean(selectedRadiusOption.metric.loading)
    return locationsLoading || colorLoading || radiusLoading
  }, [locationsLoading, selectedColorOption, selectedRadiusOption])

  const selectedColorValueLabel = useCallback(
    (propertyId: number): React.ReactNode => {
      if (selectedColorOption.type === 'fixed') return <span>—</span>
      const v = selectedColorOption.metric.getValue(propertyId, selectedPeriod)
      if (selectedColorOption.metric.renderValue) {
        return selectedColorOption.metric.renderValue(v, { currencySymbol, period: selectedPeriod })
      }
      return <span>{formatMetricValue(v, selectedColorOption.metric, currencySymbol)}</span>
    },
    [currencySymbol, selectedColorOption, selectedPeriod],
  )

  const selectedRadiusValueLabel = useCallback(
    (propertyId: number): React.ReactNode => {
      if (selectedRadiusOption.type === 'fixed') return <span>—</span>
      const v = selectedRadiusOption.metric.getValue(propertyId, selectedPeriod)
      if (selectedRadiusOption.metric.renderValue) {
        return selectedRadiusOption.metric.renderValue(v, { currencySymbol, period: selectedPeriod })
      }
      return <span>{formatMetricValue(v, selectedRadiusOption.metric, currencySymbol)}</span>
    },
    [currencySymbol, selectedPeriod, selectedRadiusOption],
  )

  const hoverTooltip = useMemo(() => {
    if (!hovered) return null
    const p = propertyById.get(hovered.id)
    if (!p) return null
    const hoverSections: Array<{ title: string; value: React.ReactNode }> = []
    if (selectedColorOption.type !== 'fixed') {
      hoverSections.push({ title: `${selectedColorOption.label} (Color)`, value: selectedColorValueLabel(p.id) })
    }
    if (selectedRadiusOption.type !== 'fixed') {
      hoverSections.push({ title: `${selectedRadiusOption.label} (Radius)`, value: selectedRadiusValueLabel(p.id) })
    }
    return (
      <Popup
        className="portfolio-hover-tooltip"
        maxWidth="320px"
        closeButton={false}
        closeOnClick={false}
        anchor="bottom"
        latitude={p.coordinates.lat}
        longitude={p.coordinates.lng}
        offset={12}
        style={{ pointerEvents: 'none' }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            {p.name}
          </Typography>
          {p.country && (
            <Typography variant="caption" color="text.secondary">
              {p.country}
            </Typography>
          )}
          {hoverSections.length > 0 && <Divider />}
          {hoverSections.length > 0 && (
            <Stack spacing={0.25}>
              {hoverSections.map((s, idx) => (
                <Stack key={idx} spacing={0.1}>
                  <Typography variant="caption" color="text.secondary">
                    {s.title}
                  </Typography>
                  <Typography variant="body2">{s.value}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Popup>
    )
  }, [
    hovered,
    propertyById,
    selectedColorOption.label,
    selectedColorOption.type,
    selectedColorValueLabel,
    selectedRadiusOption.label,
    selectedRadiusOption.type,
    selectedRadiusValueLabel,
  ])

  const openHintTooltip = useMemo(() => {
    if (!hovered || selected || !showHoverOpenHint) return null
    const p = propertyById.get(hovered.id)
    if (!p) return null
    return (
      <Popup
        className="portfolio-open-hint"
        maxWidth="220px"
        closeButton={false}
        closeOnClick={false}
        anchor="top"
        latitude={p.coordinates.lat}
        longitude={p.coordinates.lng}
        offset={14}
        style={{ pointerEvents: 'none' }}>
        Open
      </Popup>
    )
  }, [hovered, propertyById, selected, showHoverOpenHint])

  const pinnedPopup = useMemo(() => {
    if (!selected) return null
    const p = propertyById.get(selected.id)
    if (!p) return null
    const address = p.address
    const addressLine =
      address && (address.street || address.town || address.postcode)
        ? [address.street, address.town, address.postcode].filter(Boolean).join(', ')
        : null

    type PopupRow =
      | { key: string; label: string; kind: 'perPeriod'; values: Record<CanonicalPeriod, React.ReactNode> }
      | { key: string; label: string; kind: 'static'; value: React.ReactNode }

    const renderNumericValue = (metric: NumericMetricSpec, period: CanonicalPeriod): React.ReactNode => {
      const raw = metric.getValue(p.id, period)
      return metric.renderValue ? (
        metric.renderValue(raw, { currencySymbol, period })
      ) : (
        <span>{formatMetricValue(raw, metric, currencySymbol)}</span>
      )
    }

    const renderFieldValue = (field: PopupFieldSpec, period: CanonicalPeriod): React.ReactNode => {
      const raw = field.getValue(p.id, period)
      const rendered = field.renderValue
        ? field.renderValue(raw, { currencySymbol, period })
        : typeof raw === 'number'
        ? formatMetricValue(raw, field, currencySymbol)
        : formatPopupFieldValue(raw, field, currencySymbol)
      return <span>{rendered}</span>
    }

    const rows: PopupRow[] = []
    const seenMetricKeys = new Set<string>()

    if (selectedColorOption.type !== 'fixed') {
      const metric = selectedColorOption.metric
      if (!seenMetricKeys.has(metric.key)) {
        seenMetricKeys.add(metric.key)
        if (metric.perPeriod) {
          rows.push({
            key: `metric_${metric.key}`,
            label: selectedColorOption.label,
            kind: 'perPeriod',
            values: {
              [CanonicalPeriod.SHORT_TERM]: renderNumericValue(metric, CanonicalPeriod.SHORT_TERM),
              [CanonicalPeriod.MID_TERM]: renderNumericValue(metric, CanonicalPeriod.MID_TERM),
              [CanonicalPeriod.LONG_TERM]: renderNumericValue(metric, CanonicalPeriod.LONG_TERM),
            },
          })
        } else {
          rows.push({
            key: `metric_${metric.key}`,
            label: selectedColorOption.label,
            kind: 'static',
            value: renderNumericValue(metric, selectedPeriod),
          })
        }
      }
    }

    if (selectedRadiusOption.type !== 'fixed') {
      const metric = selectedRadiusOption.metric
      if (!seenMetricKeys.has(metric.key)) {
        seenMetricKeys.add(metric.key)
        if (metric.perPeriod) {
          rows.push({
            key: `metric_${metric.key}`,
            label: selectedRadiusOption.label,
            kind: 'perPeriod',
            values: {
              [CanonicalPeriod.SHORT_TERM]: renderNumericValue(metric, CanonicalPeriod.SHORT_TERM),
              [CanonicalPeriod.MID_TERM]: renderNumericValue(metric, CanonicalPeriod.MID_TERM),
              [CanonicalPeriod.LONG_TERM]: renderNumericValue(metric, CanonicalPeriod.LONG_TERM),
            },
          })
        } else {
          rows.push({
            key: `metric_${metric.key}`,
            label: selectedRadiusOption.label,
            kind: 'static',
            value: renderNumericValue(metric, selectedPeriod),
          })
        }
      }
    }

    builtInFinancialFields.forEach(f => {
      if (f.perPeriod) {
        rows.push({
          key: `fin_${f.key}`,
          label: f.label,
          kind: 'perPeriod',
          values: {
            [CanonicalPeriod.SHORT_TERM]: renderFieldValue(f, CanonicalPeriod.SHORT_TERM),
            [CanonicalPeriod.MID_TERM]: renderFieldValue(f, CanonicalPeriod.MID_TERM),
            [CanonicalPeriod.LONG_TERM]: renderFieldValue(f, CanonicalPeriod.LONG_TERM),
          },
        })
      } else {
        rows.push({ key: `fin_${f.key}`, label: f.label, kind: 'static', value: renderFieldValue(f, selectedPeriod) })
      }
    })

    popupFields.forEach(f => {
      if (f.perPeriod) {
        rows.push({
          key: `field_${f.key}`,
          label: f.label,
          kind: 'perPeriod',
          values: {
            [CanonicalPeriod.SHORT_TERM]: renderFieldValue(f, CanonicalPeriod.SHORT_TERM),
            [CanonicalPeriod.MID_TERM]: renderFieldValue(f, CanonicalPeriod.MID_TERM),
            [CanonicalPeriod.LONG_TERM]: renderFieldValue(f, CanonicalPeriod.LONG_TERM),
          },
        })
      } else {
        rows.push({
          key: `field_${f.key}`,
          label: f.label,
          kind: 'static',
          value: renderFieldValue(f, selectedPeriod),
        })
      }
    })

    const periodLabels: Record<CanonicalPeriod, string> = {
      [CanonicalPeriod.SHORT_TERM]: 'Short',
      [CanonicalPeriod.MID_TERM]: 'Mid',
      [CanonicalPeriod.LONG_TERM]: 'Long',
    }

    const selectedBg = alpha(theme.palette.primary.main, 0.12)
    const selectedBorder = alpha(theme.palette.primary.main, 0.22)
    const gridTemplate = 'minmax(120px, 1fr) repeat(3, minmax(64px, 88px))'

    return (
      <Popup
        maxWidth="420px"
        closeButton
        closeOnClick={false}
        anchor="bottom"
        latitude={selected.lat}
        longitude={selected.lng}
        offset={12}
        onClose={() => setSelected(null)}>
        <Stack spacing={1} minWidth={360}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle1" fontWeight={800}>
              {p.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[p.country, addressLine].filter(Boolean).join(' • ') || '—'}
            </Typography>
          </Stack>
          <Divider />
          <Stack spacing={0.75}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                columnGap: 1,
                alignItems: 'center',
              }}>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>
                Horizon
              </Typography>
              {PERIODS.map(period => {
                const selectedCol = period === selectedPeriod
                return (
                  <Box
                    key={period}
                    sx={{
                      px: 0.75,
                      py: 0.3,
                      borderRadius: 1,
                      border: `1px solid ${selectedCol ? selectedBorder : 'transparent'}`,
                      backgroundColor: selectedCol ? selectedBg : 'transparent',
                      textAlign: 'center',
                    }}>
                    <Typography variant="caption" fontWeight={800}>
                      {periodLabels[period]}
                    </Typography>
                  </Box>
                )
              })}
            </Box>

            <Stack spacing={0.55}>
              {rows.map(row => (
                <Box
                  key={row.key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    columnGap: 1,
                    rowGap: 0.25,
                    alignItems: 'center',
                  }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ pr: 0.5, minWidth: 0 }}
                    noWrap
                    title={row.label}>
                    {row.label}
                  </Typography>

                  {row.kind === 'perPeriod' ? (
                    PERIODS.map(period => {
                      const selectedCol = period === selectedPeriod
                      return (
                        <Box
                          key={period}
                          sx={{
                            px: 0.75,
                            py: 0.35,
                            borderRadius: 1,
                            border: `1px solid ${selectedCol ? selectedBorder : 'transparent'}`,
                            backgroundColor: selectedCol ? selectedBg : 'transparent',
                            textAlign: 'center',
                            minWidth: 0,
                          }}>
                          {row.values[period]}
                        </Box>
                      )
                    })
                  ) : (
                    <Box
                      sx={{
                        gridColumn: 'span 3',
                        px: 0.75,
                        py: 0.35,
                        borderRadius: 1,
                        border: `1px solid ${selectedBorder}`,
                        backgroundColor: selectedBg,
                        textAlign: 'center',
                        minWidth: 0,
                      }}>
                      {row.value}
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button variant="outlined" size="small" onClick={() => setSelected(null)}>
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                onViewProperty?.(p.id)
              }}>
              View property
            </Button>
          </Stack>
        </Stack>
      </Popup>
    )
  }, [
    builtInFinancialFields,
    currencySymbol,
    propertyById,
    selected,
    selectedColorOption,
    selectedPeriod,
    selectedRadiusOption,
    theme,
    onViewProperty,
    popupFields,
  ])

  const coverage = useMemo(() => {
    const total = stats.total || 0
    const colorAvailable = Math.max(0, total - (stats.noColor || 0))
    const radiusAvailable = Math.max(0, total - (stats.noRadius || 0))
    const colorPct = total ? Math.round((colorAvailable / total) * 100) : 0
    const radiusPct = total ? Math.round((radiusAvailable / total) * 100) : 0
    return { total, colorAvailable, radiusAvailable, colorPct, radiusPct }
  }, [stats.noColor, stats.noRadius, stats.total])

  const [legendTab, setLegendTab] = useState<'legend' | 'metadata'>('legend')
  const [legendExpanded, setLegendExpanded] = useState(true)

  const colorLegendNode = useMemo(() => {
    const height = 176
    if (selectedColorOption.type === 'fixed') {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 1,
              backgroundColor: selectedColorOption.color,
              border: '1px solid rgba(15, 23, 42, 0.18)',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {selectedColorOption.label}
          </Typography>
        </Stack>
      )
    }

    if (selectedColorOption.type === 'discrete') {
      const values = Object.keys(selectedColorOption.labels)
        .map(Number)
        .filter(v => Number.isFinite(v))
        .sort((a, b) => a - b)
      if (!values.length) return <Typography variant="caption">—</Typography>

      const ordered = [...values].sort((a, b) => b - a) // high -> low
      const n = ordered.length
      const ticks: LegendTick[] = ordered.map((v, i) => ({
        offsetPct: ((i + 0.5) / n) * 100,
        label: selectedColorOption.labels[v] ?? String(v),
      }))

      const legend = (
        <VerticalScaleLegend
          height={height}
          bar={
            <Stack sx={{ height: '100%', width: '100%' }} spacing={0}>
              {ordered.map(v => (
                <Box key={v} sx={{ flex: 1, backgroundColor: selectedColorOption.palette[v] ?? NO_DATA_COLOR }} />
              ))}
            </Stack>
          }
          ticks={ticks}
        />
      )

      const maxIndex = Math.max(0, values.length - 1)
      const sliderValue = colorCutoff?.kind === 'discrete' ? colorCutoff.range : ([0, maxIndex] as [number, number])
      return (
        <Stack direction="row" spacing={0.5} alignItems="stretch" sx={{ width: '100%' }}>
          <Box sx={{ minWidth: 0, flexShrink: 0 }}>{legend}</Box>
          <Slider
            orientation="vertical"
            size="small"
            disableSwap
            disabled={maxIndex <= 0}
            min={0}
            max={maxIndex}
            step={1}
            value={sliderValue}
            onChange={(_, v) => {
              if (!Array.isArray(v)) return
              setColorCutoffByKey(prev => ({
                ...prev,
                [selectedColorKey]: { kind: 'discrete', range: [v[0] as number, v[1] as number] },
              }))
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={n => {
              const idx = Math.min(maxIndex, Math.max(0, Math.round(n as number)))
              const value = values[idx]
              return selectedColorOption.labels[value] ?? String(value)
            }}
            sx={{
              height,
              py: 0,
              width: 18,
              '& .MuiSlider-rail, & .MuiSlider-track': {
                width: 4,
                borderRadius: 2,
              },
              '& .MuiSlider-rail': { opacity: 0.28 },
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.28)',
              },
            }}
          />
        </Stack>
      )
    }

    if (selectedColorOption.type === 'continuous') {
      if (!colorDomain) {
        return (
          <Typography variant="caption" color="text.secondary">
            No data
          </Typography>
        )
      }
      const { min, max } = colorDomain
      const mid = min + (max - min) * 0.5
      const lowColor = colorAtOffset(selectedColorOption.gradient, 0)
      const midColor = colorAtOffset(selectedColorOption.gradient, 0.5)
      const highColor = colorAtOffset(selectedColorOption.gradient, 1)
      const gradientCss = colorToGradient({ [min]: lowColor, [mid]: midColor, [max]: highColor }, 0)
      const ticks: LegendTick[] = [
        { offsetPct: 0, label: formatMetricValue(max, selectedColorOption.metric, currencySymbol) },
        { offsetPct: 50, label: formatMetricValue(mid, selectedColorOption.metric, currencySymbol) },
        { offsetPct: 100, label: formatMetricValue(min, selectedColorOption.metric, currencySymbol) },
      ]
      const legend = (
        <VerticalScaleLegend
          height={height}
          bar={<Box sx={{ height: '100%', width: '100%', background: gradientCss }} />}
          ticks={ticks}
        />
      )

      const sliderValue =
        colorCutoff?.kind === 'continuous'
          ? colorCutoff.range
          : ([Math.min(min, max), Math.max(min, max)] as [number, number])
      const domainMin = Math.min(min, max)
      const domainMax = Math.max(min, max)
      const step = computeSliderStep(domainMin, domainMax)

      return (
        <Stack direction="row" spacing={0.5} alignItems="stretch" sx={{ width: '100%' }}>
          <Box sx={{ minWidth: 0, flexShrink: 0 }}>{legend}</Box>
          <Slider
            orientation="vertical"
            size="small"
            disableSwap
            disabled={domainMin === domainMax}
            min={domainMin}
            max={domainMax}
            step={step}
            value={sliderValue}
            onChange={(_, v) => {
              if (!Array.isArray(v)) return
              setColorCutoffByKey(prev => ({
                ...prev,
                [selectedColorKey]: { kind: 'continuous', range: [v[0] as number, v[1] as number] },
              }))
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={n => formatMetricValue(n as number, selectedColorOption.metric, currencySymbol)}
            sx={{
              height,
              py: 0,
              width: 18,
              '& .MuiSlider-rail, & .MuiSlider-track': {
                width: 4,
                borderRadius: 2,
              },
              '& .MuiSlider-rail': { opacity: 0.28 },
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.28)',
              },
            }}
          />
        </Stack>
      )
    }

    return null
  }, [colorCutoff, colorDomain, currencySymbol, selectedColorKey, selectedColorOption])

  const radiusLegendNode = useMemo(() => {
    const circleFill = alpha(theme.palette.primary.main, 0.18)
    const circleStroke = alpha(theme.palette.text.primary, 0.22)

    if (selectedRadiusOption.type === 'fixed') {
      const d = Math.max(10, Math.min(44, selectedRadiusOption.radiusPx * 2))
      return (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: d,
              height: d,
              borderRadius: '50%',
              backgroundColor: circleFill,
              border: `1px solid ${circleStroke}`,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Default
          </Typography>
        </Stack>
      )
    }

    if (selectedRadiusOption.type === 'scaled') {
      if (!radiusDomain)
        return (
          <Typography variant="caption" color="text.secondary">
            No data
          </Typography>
        )
      const rangePx = selectedRadiusOption.rangePx ?? { min: 6, max: 24 }
      const minPx = rangePx.min
      const maxPx = rangePx.max
      const minLabel = formatMetricValue(radiusDomain.min, selectedRadiusOption.metric, currencySymbol)
      const maxLabel = formatMetricValue(radiusDomain.max, selectedRadiusOption.metric, currencySymbol)
      const domainMin = Math.min(radiusDomain.min, radiusDomain.max)
      const domainMax = Math.max(radiusDomain.min, radiusDomain.max)
      const sliderValue = radiusCutoff ?? ([domainMin, domainMax] as [number, number])
      const step = computeSliderStep(domainMin, domainMax)

      return (
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '72px 1fr 72px',
            gridTemplateRows: 'auto auto',
            columnGap: 1.25,
            rowGap: 0.35,
            alignItems: 'center',
          }}>
          <Box sx={{ display: 'grid', placeItems: 'center' }}>
            <Box
              sx={{
                width: Math.max(10, minPx * 2),
                height: Math.max(10, minPx * 2),
                borderRadius: '50%',
                backgroundColor: circleFill,
                border: `1px solid ${circleStroke}`,
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Slider
              size="small"
              disableSwap
              disabled={domainMin === domainMax}
              min={domainMin}
              max={domainMax}
              step={step}
              value={sliderValue}
              onChange={(_, v) => {
                if (!Array.isArray(v)) return
                setRadiusCutoffByKey(prev => ({
                  ...prev,
                  [selectedRadiusKey]: [v[0] as number, v[1] as number],
                }))
              }}
              valueLabelDisplay="auto"
              valueLabelFormat={n => formatMetricValue(n as number, selectedRadiusOption.metric, currencySymbol)}
              sx={{
                px: 0,
                '& .MuiSlider-rail, & .MuiSlider-track': {
                  height: 4,
                  borderRadius: 2,
                },
                '& .MuiSlider-rail': { opacity: 0.28 },
                '& .MuiSlider-track': { opacity: 0.8 },
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.28)',
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'grid', placeItems: 'center' }}>
            <Box
              sx={{
                width: Math.max(10, maxPx * 2),
                height: Math.max(10, maxPx * 2),
                borderRadius: '50%',
                backgroundColor: circleFill,
                border: `1px solid ${circleStroke}`,
              }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            {minLabel}
          </Typography>
          <Box />
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {maxLabel}
          </Typography>
        </Box>
      )
    }

    return null
  }, [
    currencySymbol,
    radiusCutoff,
    radiusDomain,
    selectedRadiusKey,
    selectedRadiusOption,
    theme.palette.primary.main,
    theme.palette.text.primary,
  ])

  const mapCursor = useMemo(() => {
    if (drawModeEnabled) return 'crosshair'
    if (selected) return 'auto'
    if (hovered) return 'pointer'
    return 'auto'
  }, [drawModeEnabled, hovered, selected])

  const selectionRingFilter = useMemo(() => {
    return ['in', ['get', 'id'], ['literal', selectedPropertyIds]] as any
  }, [selectedPropertyIds])

  return (
    <Card>
      <GlobalStyles
        styles={{
          '@keyframes portfolioOpenHintIn': {
            from: { opacity: 0, transform: 'translateY(-4px) scale(0.98)' },
            to: { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          '.portfolio-hover-tooltip.mapboxgl-popup': {
            pointerEvents: 'none',
          },
          '.portfolio-hover-tooltip .mapboxgl-popup-content': {
            pointerEvents: 'none',
          },
          '.portfolio-hover-tooltip .mapboxgl-popup-tip': {
            pointerEvents: 'none',
          },
          '.portfolio-open-hint.mapboxgl-popup': {
            pointerEvents: 'none',
          },
          '.portfolio-open-hint .mapboxgl-popup-content': {
            padding: '4px 10px',
            borderRadius: '999px',
            backgroundColor: alpha(theme.palette.grey[800], 0.84),
            color: theme.palette.common.white,
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.18)',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '0.2px',
            backdropFilter: 'blur(6px)',
            animation: 'portfolioOpenHintIn 140ms ease-out',
          },
          '.portfolio-open-hint .mapboxgl-popup-tip': {
            display: 'none',
          },
        }}
      />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <PortfolioSectionTitle title={title} />

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            {mergedColorOptions.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="map-color-select-label">Color</InputLabel>
                <Select
                  labelId="map-color-select-label"
                  label="Color"
                  value={selectedColorKey}
                  onChange={e => setSelectedColorKey(e.target.value as string)}>
                  {mergedColorOptions.map(opt => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.type === 'fixed' ? opt.label : renderOptionLabel(opt.label, opt.metric.perPeriod)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {mergedRadiusOptions.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="map-radius-select-label">Radius</InputLabel>
                <Select
                  labelId="map-radius-select-label"
                  label="Radius"
                  value={selectedRadiusKey}
                  onChange={e => setSelectedRadiusKey(e.target.value as string)}>
                  {mergedRadiusOptions.map(opt => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.type === 'fixed' ? opt.label : renderOptionLabel(opt.label, opt.metric.perPeriod)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="map-hide-select-label">No data</InputLabel>
              <Select
                labelId="map-hide-select-label"
                label="No data"
                value={hideMode}
                onChange={e => setHideMode(e.target.value as MissingDataHideMode)}>
                <MenuItem value="none">Don&apos;t hide</MenuItem>
                <MenuItem value="both">Hide if both missing</MenuItem>
                <MenuItem value="any">Hide if any missing</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              variant={drawModeEnabled ? 'contained' : 'outlined'}
              onClick={() => {
                if (drawModeEnabled) {
                  exitDrawMode()
                  return
                }
                setDrawModeEnabled(true)
                setSelectAllActive(false)
                setSelectionPolygon(null)
                setSelectionConfirmed(false)
                drawRef.current?.changeMode('draw_polygon')
              }}>
              {drawModeEnabled ? 'Exit selection' : 'Select properties'}
            </Button>
          </Stack>
        </Stack>

        <Collapse in={drawModeEnabled} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1.25 }}>
            <Paper
              variant="outlined"
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.035),
                borderColor: alpha(theme.palette.primary.main, 0.14),
              }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Stack spacing={0.15} minWidth={0}>
                  <Typography variant="caption" fontWeight={900}>
                    Group properties
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectAllActive
                      ? `Selected all ${selectedPropertyIds.length.toLocaleString()} properties.`
                      : selectionPolygon
                      ? `Selected ${selectedPropertyIds.length.toLocaleString()} properties`
                      : 'Draw an area enclosing properties you wish to group (double-click to finish), then confirm your selection.'}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {selectionConfirmed ? (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={selectedPropertyIds.length === 0}
                        onClick={() => setAssignDialogOpen(true)}>
                        Assign to group
                      </Button>
                      <Button size="small" variant="outlined" onClick={clearDrawSelection}>
                        Change selection
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={properties.length === 0}
                        onClick={selectAllProperties}>
                        Select all
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!hasPendingSelection || selectedPropertyIds.length === 0}
                        onClick={confirmDrawSelection}>
                        Confirm selection
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!hasPendingSelection}
                        onClick={clearDrawSelection}>
                        Clear
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ height, width: '100%', position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 2 }}>
          <Paper
            elevation={3}
            sx={{
              width: { xs: 'calc(100vw - 32px)', sm: 320 },
              maxWidth: 360,
              backgroundColor: alpha(theme.palette.background.paper, 0.92),
              overflow: 'hidden',
            }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, pr: 0.5, pt: 0.25 }}>
              <Tabs
                value={legendTab}
                onChange={(_, v) => setLegendTab(v)}
                sx={{
                  flex: 1,
                  minHeight: 36,
                  '& .MuiTab-root': {
                    minHeight: 36,
                    py: 0.5,
                    px: 1,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: 12,
                    minWidth: 72,
                  },
                }}>
                <Tab value="legend" label="Legend" />
                <Tab value="metadata" label="Metadata" />
              </Tabs>
              <IconButton
                size="small"
                onClick={() => setLegendExpanded(v => !v)}
                sx={{ borderRadius: 1, color: 'text.secondary' }}>
                {legendExpanded ? (
                  <KeyboardArrowDownRoundedIcon fontSize="small" />
                ) : (
                  <KeyboardArrowUpRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Stack>

            <Collapse in={legendExpanded} timeout="auto">
              <Divider sx={{ mt: 0.5 }} />
              <Box sx={{ p: 1.5 }}>
                {legendTab === 'legend' ? (
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1,
                        py: 0.6,
                        borderRadius: 1.5,
                        backgroundColor: alpha(theme.palette.text.primary, 0.035),
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                      }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={900}>
                        Properties visible
                      </Typography>
                      <Typography variant="caption" fontWeight={900}>
                        {(stats.shown ?? 0).toLocaleString()} / {(stats.total ?? 0).toLocaleString()}
                      </Typography>
                    </Box>

                    <Stack spacing={0.75}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        fontWeight={900}
                        sx={{ lineHeight: 1, letterSpacing: '0.12em' }}>
                        COLOR
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={900}
                        title={selectedColorOption.label}
                        sx={{
                          lineHeight: 1.2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                        {selectedColorOption.label}
                      </Typography>
                      {colorLegendNode}
                    </Stack>

                    <Divider />

                    <Stack spacing={0.75}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        fontWeight={900}
                        sx={{ lineHeight: 1, letterSpacing: '0.12em' }}>
                        RADIUS
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={900}
                        title={selectedRadiusOption.label}
                        sx={{
                          lineHeight: 1.2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                        {selectedRadiusOption.label}
                      </Typography>
                      {radiusLegendNode}
                    </Stack>

                    <Divider />

                    <Stack spacing={0.6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800}>
                        Note
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        {renderStaticBadge()}
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                          Value doesn&apos;t change across periods.
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={1.25}>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>
                      Coverage
                    </Typography>
                    <Stack spacing={0.75}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                          {selectedColorOption.label}
                        </Typography>
                        <Typography variant="caption">
                          {coverage.colorAvailable}/{coverage.total} ({coverage.colorPct}%) properties
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                          {selectedRadiusOption.label}
                        </Typography>
                        <Typography variant="caption">
                          {coverage.radiusAvailable}/{coverage.total} ({coverage.radiusPct}%) properties
                        </Typography>
                      </Stack>
                    </Stack>

                    <Divider />

                    <Typography variant="caption" color="text.secondary" fontWeight={800}>
                      Missing data
                    </Typography>
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: 1,
                            backgroundColor: NO_DATA_COLOR,
                            border: '1px solid rgba(15, 23, 42, 0.18)',
                            display: 'grid',
                            placeItems: 'center',
                            color: '#4a4a4a',
                            fontSize: 12,
                            fontWeight: 800,
                            lineHeight: 1,
                          }}>
                          {NO_COLOR_X}
                        </Box>
                        <Stack spacing={0.1} minWidth={0}>
                          <Typography variant="caption" fontWeight={900}>
                            No color
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                            Grey fill with {NO_COLOR_X} symbol.
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            backgroundColor: alpha(theme.palette.text.primary, 0.14),
                            border: '2px solid #ffffff',
                            outline: '1px solid rgba(15, 23, 42, 0.18)',
                          }}
                        />
                        <Stack spacing={0.1} minWidth={0}>
                          <Typography variant="caption" fontWeight={900}>
                            No radius
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                            White edge around circle.
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Collapse>
          </Paper>
        </Box>
        <MapGL
          ref={mapRef}
          reuseMaps
          mapStyle={mapStyle}
          mapboxAccessToken={ConfigProvider.mapboxPublicKey}
          onLoad={() => setMapLoaded(true)}
          cursor={mapCursor}
          onMouseMove={handleHover}
          onClick={handleClick}
          interactiveLayerIds={layerIds}
          initialViewState={{
            longitude: properties[0]?.coordinates.lng ?? -2.3,
            latitude: properties[0]?.coordinates.lat ?? 51.8,
            zoom: 5.5,
            pitch: 0,
            bearing: 0,
          }}
          style={{ width: '100%', height: '100%', borderRadius: '0 0 8px 8px' }}>
          {selectedMetricLoading && (
            <Stack
              justifyContent="center"
              alignItems="center"
              width="100%"
              height="100%"
              top={0}
              left={0}
              zIndex={1}
              position={'absolute'}
              bgcolor={alpha(theme.palette.background.paper, 0.35)}>
              <CircularProgress size={28} />
            </Stack>
          )}

          <Source id="portfolio-properties" type="geojson" data={featureCollection}>
            <Layer
              id="portfolio-draw-selection-ring"
              type="circle"
              filter={selectionRingFilter}
              paint={{
                'circle-radius': ['+', ['get', 'radiusPx'], 6],
                'circle-color': 'rgba(0,0,0,0)',
                'circle-opacity': 1,
                'circle-stroke-width': 2.25,
                'circle-stroke-color': alpha(theme.palette.info.main, 0.9),
              }}
            />
            <Layer
              id="portfolio-property-circles"
              type="circle"
              paint={{
                'circle-color': ['get', 'fillColor'],
                'circle-opacity': 0.85,
                'circle-radius': ['get', 'radiusPx'],
                'circle-stroke-width': [
                  'case',
                  ['==', ['get', 'id'], selectedMatchId],
                  3.25,
                  ['==', ['get', 'id'], hoverMatchId],
                  2.75,
                  ['get', 'strokeWidth'],
                ],
                'circle-stroke-color': [
                  'case',
                  ['==', ['get', 'id'], selectedMatchId],
                  '#ffffff',
                  ['==', ['get', 'id'], hoverMatchId],
                  '#ffffff',
                  ['get', 'strokeColor'],
                ],
              }}
            />
            <Layer
              id="portfolio-no-color-x"
              type="symbol"
              filter={['==', ['get', 'hasColor'], false]}
              layout={{
                'text-field': NO_COLOR_X,
                'text-size': 14,
                'text-allow-overlap': true,
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
              }}
              paint={{
                'text-color': '#4a4a4a',
                'text-halo-color': 'rgba(255,255,255,0.65)',
                'text-halo-width': 1,
              }}
            />
          </Source>

          {pinnedPopup}
          {!selected && hoverTooltip}
          {openHintTooltip}
          {drawModeEnabled && (
            <DrawControl
              drawRef={drawRef}
              position="top-right"
              displayControlsDefault={false}
              controls={{}}
              defaultMode="draw_polygon"
              onCreate={handleDrawCreateOrUpdate}
              onUpdate={handleDrawCreateOrUpdate}
              onDelete={handleDrawDelete}
            />
          )}
          <ScaleControl position="bottom-right" />
          <NavigationControl position="top-right" />
        </MapGL>
      </Box>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign to property group</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Selected {selectedPropertyIds.length.toLocaleString()} properties.
            </Typography>

            <Tabs
              value={assignDialogTab}
              onChange={(_, v) => setAssignDialogTab(v)}
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  minHeight: 40,
                  py: 0.75,
                  textTransform: 'none',
                  fontWeight: 800,
                },
              }}>
              <Tab value="existing" label="Existing group" />
              <Tab value="new" label="Create new" />
            </Tabs>

            {assignDialogTab === 'existing' ? (
              locationGroupsLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                </Stack>
              ) : sortedGroups.length === 0 ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    No groups found for your organisation.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setAssignDialogTab('new')}
                    sx={{ width: 'fit-content' }}>
                    Create a new group
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1.25}>
                  <Autocomplete
                    options={sortedGroups}
                    value={existingGroup}
                    onChange={(_, v) => setExistingGroup(v)}
                    getOptionLabel={o => o?.name ?? ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%">
                          <Stack minWidth={0}>
                            <Typography variant="body2" fontWeight={800} noWrap title={option.name}>
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {option.type?.name ?? option.type?.type ?? '—'}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" fontWeight={800}>
                            {(option.property_count ?? 0).toLocaleString()}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    renderInput={params => <TextField {...params} label="Group" size="small" />}
                  />

                  <Typography variant="caption" color="text.secondary">
                    Adds the selected properties to the chosen group.
                  </Typography>
                </Stack>
              )
            ) : (
              <Stack spacing={1.25}>
                <TextField
                  label="Group name"
                  size="small"
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  autoFocus
                />
                <TextField
                  label="Description (optional)"
                  size="small"
                  value={newGroupDescription}
                  onChange={e => setNewGroupDescription(e.target.value)}
                  multiline
                  minRows={2}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectCreatedGroup}
                      onChange={e => setSelectCreatedGroup(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={800}>
                      Set as active portfolio
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Creates a new group and assigns the selected properties to it.
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setAssignDialogOpen(false)} disabled={assignSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitAssignment}
            disabled={!canSubmitAssignment}
            startIcon={assignSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}>
            {assignDialogTab === 'existing' ? 'Add to group' : 'Create group'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
